/**
 * LoginPage.jsx — shared/LoginPage.jsx
 *
 * Full-screen login gate. Shown when no valid session token exists in localStorage.
 * On success, calls onLogin() which flips the App-level loggedIn state.
 * Credentials are validated server-side in the Worker — this form just sends them.
 */

import { useState } from 'react';
import { login, setSession } from '../../lib/api';

export default function LoginPage({ onLogin }) {
  const [user,    setUser]    = useState('');
  const [pass,    setPass]    = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(user.trim(), pass);
      setSession(res.token || '', res.username || user.trim(), res.role || 'guest');
      onLogin();
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Input style — shared between username and password fields
  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 5, boxSizing: 'border-box',
    border: '1px solid var(--border)', background: 'var(--bg)',
    color: 'var(--text)', fontSize: 14, fontFamily: 'monospace', outline: 'none',
  };

  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', fontFamily: "var(--sans, 'Inter', system-ui, sans-serif)",
    }}>
      <div style={{
        width: 360, background: 'var(--bg-elev)',
        border: '1px solid var(--border)', borderRadius: 10,
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
      }}>
        {/* Branding header */}
        <div style={{ padding: '28px 28px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
          <div style={{ fontSize: 11, letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>
            <span style={{ color: 'var(--accent)' }}>▸</span> Options Desk
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Sign in</div>
          <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 4 }}>Private trading dashboard</div>
        </div>

        <div style={{ padding: 28 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>Username</label>
            <input
              type="text" value={user} onChange={e => setUser(e.target.value)}
              autoComplete="username" autoFocus style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'var(--accent)'}
              onBlur={e  => e.target.style.borderColor = 'var(--border)'}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>Password</label>
            <input
              type="password" value={pass} onChange={e => setPass(e.target.value)}
              autoComplete="current-password" onKeyDown={e => e.key === 'Enter' && submit(e)}
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'var(--accent)'}
              onBlur={e  => e.target.style.borderColor = 'var(--border)'}
            />
          </div>

          {error && (
            <div style={{ background: 'var(--down-soft)', color: 'var(--down)', fontSize: 12, padding: '8px 12px', borderRadius: 5, marginBottom: 16 }}>
              ⚠ {error}
            </div>
          )}

          <button
            onClick={submit} disabled={loading || !user || !pass}
            style={{
              width: '100%', padding: 10, borderRadius: 5, border: 'none',
              background: loading || !user || !pass ? 'var(--border)' : 'var(--accent)',
              color: loading || !user || !pass ? 'var(--faint)' : 'var(--bg)',
              fontSize: 13, fontWeight: 700, letterSpacing: '.06em',
              cursor: loading || !user || !pass ? 'not-allowed' : 'pointer',
              transition: 'background .15s',
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}
